import {
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Box,
  Card,
  CardContent,
  Paper,
  Fade,
  Grow,
  Zoom,
  useMediaQuery,
  useTheme,
  alpha,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  LocalShipping as TruckIcon,
  Warehouse as WarehouseIcon,
  Security as SecurityIcon,
  Place as PlaceIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

// Import your images
import heroImage from "../assets/images/b.png";
import truckImage from "../assets/images/b.png"; // You might want to add this image

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Brand colors
  const brandColors = {
    green: '#a6ce39',
    black: '#050606',
    orange: '#ec7723',
    white: '#ffffff'
  };

  const services = [
    {
      icon: <TruckIcon />,
      title: "Road Transport",
      description: "Reliable cross-border transportation services within and outside Eswatini.",
      features: ["GPS Tracking", "Experienced Drivers", "Real-time Updates"],
      color: brandColors.orange
    },
    {
      icon: <WarehouseIcon />,
      title: "Logistics Solutions",
      description: "Customized transportation networks for cost-effective business solutions.",
      features: ["Custom Solutions", "Multi-sector", "Efficient Routing"],
      color: brandColors.green
    },
    {
      icon: <SecurityIcon />,
      title: "Secure Transport",
      description: "C-tracking systems for enhanced security and safety management.",
      features: ["24/7 Monitoring", "Secure Fleet", "Safety Protocols"],
      color: brandColors.orange
    },
    {
      icon: <PlaceIcon />,
      title: "Nationwide Coverage",
      description: "Extensive network covering Eswatini with international connections.",
      features: ["Cross-border", "Timely Delivery", "Regional Network"],
      color: brandColors.green
    },
  ];

  const stats = [
    { 
      value: "2021", 
      label: "Established", 
      icon: <TrendingIcon />,
      description: "Years of Excellence",
      color: brandColors.orange
    },
    { 
      value: "15+", 
      label: "Fleet Vehicles", 
      icon: <TruckIcon />,
      description: "Modern Equipment",
      color: brandColors.green
    },
    { 
      value: "14+", 
      label: "Team Members", 
      icon: <GroupsIcon />,
      description: "Professional Staff",
      color: brandColors.orange
    },
    { 
      value: "24/7", 
      label: "Support", 
      icon: <ShieldIcon />,
      description: "Always Available",
      color: brandColors.green
    },
  ];

  const values = [
    { icon: <VerifiedIcon />, title: "Reliability", description: "Consistent on-time delivery" },
    { icon: <CheckIcon />, title: "Quality", description: "High service standards" },
    { icon: <ShieldIcon />, title: "Safety", description: "Secure transportation" },
    { icon: <BusinessIcon />, title: "Professionalism", description: "Expert team" },
  ];

  const clients = [
    "Cargo Carriers",
    "RES Corporation",
    "EEC",
    "USA Distillers",
    "Isanku Logistics",
    "Ubombo Sugar",
    "Inyatsi Construction",
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          color: brandColors.white,
          pt: { xs: 6, md: 8 },
          pb: { xs: 12, md: 20 },
          minHeight: { xs: "90vh", md: "100vh" },
          display: "flex",
          alignItems: "center",
          backgroundImage: `linear-gradient(135deg, ${alpha(brandColors.black, 0.85)} 0%, ${alpha(brandColors.black, 0.7)} 100%), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 30%, ${alpha(brandColors.orange, 0.1)} 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, ${alpha(brandColors.green, 0.05)} 0%, transparent 50%)
            `,
            zIndex: 1,
          }}
        />

        {/* Floating Elements */}
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            right: "10%",
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(brandColors.orange, 0.2)} 0%, transparent 70%)`,
            animation: "float 6s ease-in-out infinite",
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-20px)" },
            },
          }}
        />
        
        <Box
          sx={{
            position: "absolute",
            bottom: "30%",
            left: "5%",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(brandColors.green, 0.15)} 0%, transparent 70%)`,
            animation: "float 4s ease-in-out infinite",
            animationDelay: "1s",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Fade in timeout={800}>
                <Box>
                  <Chip
                    label="EST. 2021"
                    icon={<StarIcon />}
                    sx={{
                      mb: 4,
                      backgroundColor: alpha(brandColors.green, 0.2),
                      color: brandColors.green,
                      border: `1px solid ${alpha(brandColors.green, 0.3)}`,
                      px: 2,
                      py: 1,
                      fontSize: "0.9rem",
                      fontWeight: 700,
                      backdropFilter: "blur(10px)",
                      '& .MuiChip-icon': {
                        color: brandColors.green,
                      }
                    }}
                  />

                  <Typography
                    variant="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 900,
                      mb: 3,
                      fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem" },
                      background: `linear-gradient(135deg, ${brandColors.white} 0%, ${brandColors.green} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      lineHeight: 1.1,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Precision Logistics
                    <br />
                    <Typography
                      component="span"
                      variant="h1"
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4.5rem" },
                        color: brandColors.white,
                        textShadow: "0 2px 20px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      Excellence in Motion
                    </Typography>
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      mb: 4,
                      color: alpha(brandColors.white, 0.9),
                      fontWeight: 500,
                      lineHeight: 1.6,
                      fontSize: { xs: "1.1rem", md: "1.3rem" },
                    }}
                  >
                    A sister company of HYDROPCE HYDRAULICS AND PNEUMATICS ENGINEERING
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      maxWidth: 700,
                      mb: 6,
                      color: alpha(brandColors.white, 0.8),
                      fontWeight: 400,
                      lineHeight: 1.8,
                      fontSize: { xs: "1rem", md: "1.2rem" },
                    }}
                  >
                    Leading transport company in The Kingdom of Eswatini providing added value 
                    services in road transport from any destination, within or outside Eswatini.
                  </Typography>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={3}
                    sx={{ mt: 6 }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      component={Link}
                      to="/booking"
                      endIcon={<ArrowIcon />}
                      sx={{
                        px: { xs: 4, md: 6 },
                        py: { xs: 1.8, md: 2.2 },
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        borderRadius: "14px",
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                        boxShadow: `0 8px 32px ${alpha(brandColors.orange, 0.4)}`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
                          transform: "translateY(-4px)",
                          boxShadow: `0 12px 40px ${alpha(brandColors.orange, 0.5)}`,
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      Get Quote
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      component={Link}
                      to="/contact"
                      startIcon={<PhoneIcon />}
                      sx={{
                        px: { xs: 4, md: 6 },
                        py: { xs: 1.8, md: 2.2 },
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        borderWidth: 2,
                        color: brandColors.white,
                        borderColor: alpha(brandColors.white, 0.3),
                        borderRadius: "14px",
                        fontWeight: 700,
                        backgroundColor: alpha(brandColors.black, 0.3),
                        backdropFilter: "blur(10px)",
                        "&:hover": {
                          borderColor: brandColors.white,
                          backgroundColor: alpha(brandColors.white, 0.1),
                          transform: "translateY(-4px)",
                          backdropFilter: "blur(20px)",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      Contact Us
                    </Button>
                  </Stack>

                  {/* Quick Contact Info */}
                  <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                      mt: 6,
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <PhoneIcon sx={{ color: brandColors.green, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: alpha(brandColors.white, 0.6), display: "block" }}>
                          Call Us
                        </Typography>
                        <Typography variant="body2" sx={{ color: brandColors.white, fontWeight: 600 }}>
                          +268 7602 1597
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <EmailIcon sx={{ color: brandColors.green, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: alpha(brandColors.white, 0.6), display: "block" }}>
                          Email
                        </Typography>
                        <Typography variant="body2" sx={{ color: brandColors.white, fontWeight: 600 }}>
                          transcologistics26@gmail.com
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={5}>
              <Zoom in timeout={1000}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "24px",
                    background: `linear-gradient(135deg, ${alpha(brandColors.white, 0.1)} 0%, ${alpha(brandColors.white, 0.05)} 100%)`,
                    backdropFilter: "blur(20px)",
                    border: `1px solid ${alpha(brandColors.white, 0.1)}`,
                    position: "relative",
                    overflow: "hidden",
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.green} 100%)`,
                    }
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      mb: 3,
                      color: brandColors.white,
                      fontWeight: 700,
                    }}
                  >
                    Why Choose TRANSCO?
                  </Typography>
                  
                  <Stack spacing={3}>
                    {values.map((value, index) => (
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          background: alpha(brandColors.white, 0.05),
                          border: `1px solid ${alpha(brandColors.white, 0.1)}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background: alpha(brandColors.white, 0.08),
                            borderColor: brandColors.green,
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            background: alpha(brandColors.orange, 0.2),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: brandColors.orange,
                          }}
                        >
                          {value.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: brandColors.white, fontWeight: 700 }}>
                            {value.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha(brandColors.white, 0.7) }}>
                            {value.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 10, md: 15 }, position: "relative" }}>
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 10% 20%, ${alpha(brandColors.green, 0.03)} 0%, transparent 50%)`,
            zIndex: -1,
          }}
        />
        
        <Box textAlign="center" mb={{ xs: 8, md: 12 }}>
          <Chip
            label="OUR SERVICES"
            sx={{
              mb: 3,
              backgroundColor: alpha(brandColors.orange, 0.1),
              color: brandColors.orange,
              border: `1px solid ${alpha(brandColors.orange, 0.3)}`,
              px: 3,
              py: 1,
              fontSize: "0.8rem",
              fontWeight: 700,
              letterSpacing: "1px",
            }}
          />
          
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 900,
              color: brandColors.black,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              mb: 3,
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -16,
                left: "50%",
                transform: "translateX(-50%)",
                width: 100,
                height: 4,
                background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.green} 100%)`,
                borderRadius: 2,
              },
            }}
          >
            Comprehensive Logistics Solutions
          </Typography>
          
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 800,
              mx: "auto",
              mt: 4,
              lineHeight: 1.8,
              fontWeight: 400,
              fontSize: { xs: "1.1rem", md: "1.2rem" },
            }}
          >
            As a fully integrated transport provider, we make your transportation network 
            more cost-effective regardless of your business sector.
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Grow in timeout={(index + 1) * 300}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: "20px",
                    border: "1px solid",
                    borderColor: alpha(brandColors.black, 0.1),
                    background: brandColors.white,
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "&:hover": {
                      borderColor: service.color,
                      transform: "translateY(-12px)",
                      boxShadow: `0 20px 40px ${alpha(service.color, 0.15)}`,
                      "& .service-icon": {
                        transform: "scale(1.1) rotate(5deg)",
                        background: service.color,
                      },
                      "& .service-features": {
                        maxHeight: "200px",
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                    <Box
                      className="service-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        mx: "auto",
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: alpha(service.color, 0.1),
                        borderRadius: "20px",
                        transition: "all 0.3s ease",
                        "& svg": {
                          fontSize: 36,
                          color: service.color,
                        },
                      }}
                    >
                      {service.icon}
                    </Box>

                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      fontWeight={800}
                      sx={{ 
                        color: brandColors.black,
                        mb: 2,
                      }}
                    >
                      {service.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6, minHeight: "4.8em" }}
                    >
                      {service.description}
                    </Typography>

                    <Box
                      className="service-features"
                      sx={{
                        maxHeight: 0,
                        opacity: 0,
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Divider sx={{ my: 2, borderColor: alpha(service.color, 0.2) }} />
                      
                      <Stack spacing={1} alignItems="flex-start">
                        {service.features.map((feature, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{ width: '100%' }}
                          >
                            <CheckIcon
                              sx={{
                                fontSize: 16,
                                color: service.color,
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Stats Section */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 12, md: 18 },
          background: `linear-gradient(135deg, ${brandColors.black} 0%, ${alpha(brandColors.black, 0.95)} 100%)`,
          color: brandColors.white,
          overflow: "hidden",
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 30%, ${alpha(brandColors.orange, 0.1)} 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, ${alpha(brandColors.green, 0.05)} 0%, transparent 50%)
            `,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, md: 8 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Zoom in timeout={(index + 1) * 400}>
                  <Box textAlign="center">
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        mx: "auto",
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: alpha(stat.color, 0.1),
                        borderRadius: "20px",
                        border: `1px solid ${alpha(stat.color, 0.2)}`,
                        position: "relative",
                        "& svg": {
                          fontSize: 32,
                          color: stat.color,
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: -2,
                          left: -2,
                          right: -2,
                          bottom: -2,
                          borderRadius: "20px",
                          background: `linear-gradient(135deg, ${alpha(stat.color, 0.2)} 0%, transparent 50%)`,
                          zIndex: -1,
                        },
                      }}
                    >
                      {stat.icon}
                    </Box>

                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 900,
                        mb: 1,
                        background: `linear-gradient(135deg, ${brandColors.white} 0%, ${stat.color} 100%)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        fontSize: { xs: "3.5rem", md: "4rem" },
                        lineHeight: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: brandColors.white,
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      {stat.label}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha(brandColors.white, 0.7),
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        fontWeight: 500,
                      }}
                    >
                      {stat.description}
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Trusted Clients Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 10, md: 15 } }}>
        <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 900,
              color: brandColors.black,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              mb: 3,
            }}
          >
            Trusted by Industry Leaders
          </Typography>
          
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: "auto",
              mb: 6,
              lineHeight: 1.8,
              fontWeight: 400,
            }}
          >
            We proudly serve some of the most reputable companies in Southern Africa
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {clients.map((client, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Grow in timeout={(index + 1) * 200}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    textAlign: "center",
                    background: alpha(brandColors.green, 0.03),
                    border: `1px solid ${alpha(brandColors.green, 0.1)}`,
                    transition: "all 0.3s ease",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      background: alpha(brandColors.orange, 0.05),
                      borderColor: brandColors.orange,
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 24px ${alpha(brandColors.orange, 0.1)}`,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: brandColors.black,
                    }}
                  >
                    {client}
                  </Typography>
                </Paper>
              </Grow>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box
          sx={{
            mt: { xs: 12, md: 15 },
            p: { xs: 4, md: 6 },
            borderRadius: "24px",
            background: `linear-gradient(135deg, ${alpha(brandColors.orange, 0.1)} 0%, ${alpha(brandColors.green, 0.05)} 100%)`,
            border: `1px solid ${alpha(brandColors.orange, 0.2)}`,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 900,
              color: brandColors.black,
              fontSize: { xs: "2rem", md: "2.5rem" },
              mb: 3,
            }}
          >
            Ready to Optimize Your Logistics?
          </Typography>
          
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: "auto",
              mb: 4,
              lineHeight: 1.8,
              fontWeight: 400,
            }}
          >
            Get a customized transportation solution that fits your business needs and budget
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/contact"
              startIcon={<PhoneIcon />}
              sx={{
                px: { xs: 4, md: 6 },
                py: { xs: 1.8, md: 2.2 },
                fontSize: "1rem",
                borderRadius: "14px",
                fontWeight: 700,
                background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.9)} 100%)`,
                boxShadow: `0 8px 24px ${alpha(brandColors.orange, 0.3)}`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${brandColors.orange} 100%)`,
                  transform: "translateY(-4px)",
                  boxShadow: `0 12px 32px ${alpha(brandColors.orange, 0.4)}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              Contact Our Team
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/booking"
              endIcon={<ArrowIcon />}
              sx={{
                px: { xs: 4, md: 6 },
                py: { xs: 1.8, md: 2.2 },
                fontSize: "1rem",
                borderWidth: 2,
                color: brandColors.orange,
                borderColor: brandColors.orange,
                borderRadius: "14px",
                fontWeight: 700,
                background: brandColors.white,
                "&:hover": {
                  background: alpha(brandColors.orange, 0.05),
                  borderWidth: 2,
                  transform: "translateY(-4px)",
                  boxShadow: `0 8px 24px ${alpha(brandColors.orange, 0.15)}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              Get Free Quote
            </Button>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default Home;